import { useEffect, useRef } from "react";
import gsap from "gsap";
import type { LoaderProps } from "../types";

const R = 86;
const CIRC = 2 * Math.PI * R;

/** Couple initials from a title like "Deniz & Arda" → ["D", "A"]. */
function deriveInitials(title: string): string[] {
  return title
    .split("&")
    .map((s) => s.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2);
}

/**
 * ONYX media loader — a hairline ring that fills with the preload progress,
 * a serif monogram of the couple's initials at its centre, then a soft GSAP
 * fade-out that reveals the hero. Onyx palette only (bg + crimson accent).
 *
 * Registered as Onyx's theme loader; receives the shared `LoaderProps` from
 * `PremiumLayoutHost`.
 */
export function OnyxLoader({ progress, ready, title, accent, bg, label, onExited }: LoaderProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const monoRef = useRef<HTMLDivElement>(null);
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);
  const initials = deriveInitials(title);

  useEffect(() => {
    if (!ready) return;
    const overlay = overlayRef.current;
    if (!overlay) { onExited(); return; }
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    const tl = gsap.timeline({ onComplete: onExited });
    if (reduce) {
      tl.to(overlay, { opacity: 0, duration: 0.45, ease: "power2.out" });
    } else {
      tl.to(monoRef.current, { scale: 1.08, opacity: 0, filter: "blur(6px)", duration: 0.7, ease: "power2.in" }, 0)
        .to(overlay, { opacity: 0, duration: 0.6, ease: "power2.inOut" }, 0.2);
    }
    return () => { tl.kill(); };
  }, [ready, onExited]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center text-white"
      style={{ background: bg, fontFamily: "'Inter', system-ui, sans-serif" }}
      role="status"
      aria-live="polite"
      aria-busy={!ready}
    >
      <div ref={monoRef} className="relative flex items-center justify-center" style={{ width: 200, height: 200 }}>
        <svg width="200" height="200" viewBox="0 0 200 200" className="absolute inset-0">
          <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
          <circle
            cx="100"
            cy="100"
            r={R}
            fill="none"
            stroke={accent}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - Math.min(1, Math.max(0, progress)))}
            transform="rotate(-90 100 100)"
            style={{ transition: "stroke-dashoffset 0.4s ease" }}
          />
        </svg>
        <div
          className="flex items-baseline gap-1 font-light leading-none tracking-[-0.02em]"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(2.5rem, 9vw, 3.5rem)" }}
        >
          {initials.length >= 2 ? (
            <>
              <span>{initials[0]}</span>
              <span className="text-[0.55em]" style={{ color: accent }}>&amp;</span>
              <span>{initials[1]}</span>
            </>
          ) : (
            <span>{initials[0] ?? ""}</span>
          )}
        </div>
      </div>

      {label && <p className="mt-10 text-[10px] uppercase tracking-[0.5em] text-white/40">{label}</p>}
      <p className={`${label ? "mt-3" : "mt-10"} text-[10px] tabular-nums tracking-[0.3em]`} style={{ color: accent }}>
        {String(pct).padStart(2, "0")}
      </p>

      <span className="sr-only">Yükleniyor… %{pct}</span>
    </div>
  );
}
